/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useCallback } from 'react'
import { Modal } from 'antd'
import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import Button from '../../ts-components/Button'

/**
 * @interface AudienceBreakdownData
 * @property {number} total_users - Total number of users in the audience
 * @property {number} following_users - Number of users following the entity
 * @property {number} player_users - Number of player users
 * @property {number} official_users - Number of official users
 * @property {number} team_official_users - Number of team official users
 * @property {number} league_admin_users - Number of league admin users
 * @property {number} videographer_users - Number of videographer users
 * @property {number} admin_users - Number of admin users
 * @property {number} owner_users - Number of owner users
 * @property {number} unique_users - Number of unique users
 */
export interface AudienceBreakdownData {
  total_users: number
  unique_users: number
  following_users: number
  player_users: number
  official_users: number
  team_official_users: number
  league_admin_users: number
  videographer_users: number
  admin_users: number
  owner_users: number
}

interface NotificationWarningModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  sendViaEmail: boolean
  sendViaPush: boolean
  isLoading: boolean
  audienceData: AudienceBreakdownData | null
  isLoadingAudience: boolean
}

/**
 * Notification Warning Modal component - warns users about sending notifications to audience
 */
export const NotificationWarningModal: React.FC<
  NotificationWarningModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  sendViaEmail,
  sendViaPush,
  isLoading,
  audienceData,
  isLoadingAudience,
}) => {
  const getNotificationTypes = () => {
    const types: string[] = []
    if (sendViaEmail) {
      types.push('email')
    }
    if (sendViaPush) {
      types.push('push notification')
    }
    return types.join(' and ')
  }

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        margin: 24px;
        padding: 0;
        max-width: 480px;
        .ant-modal-content {
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={onClose}
      centered
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.WARNING_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading3'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            text-align: center;
          `}
        >
          Share This Update With The Selected Audience?
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 16px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              You are about to send{' '}
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                {getNotificationTypes()}
              </span>{' '}
              notifications to all users in your selected audience. This action
              cannot be undone.
            </Text>
          </div>

          {/* Audience Breakdown */}
          <div
            css={css`
              align-self: stretch;
              background: ${colors.SECONDARY_200};
              border-radius: 8px;
              padding: 16px;
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='semiBold'
              color={colors.WHITE}
            >
              Target Audience Breakdown
            </Text>

            {isLoadingAudience ? (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                Loading audience data...
              </Text>
            ) : audienceData ? (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                `}
              >
                {audienceData.admin_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Admins: {audienceData.admin_users.toLocaleString()} User
                    {audienceData.admin_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.league_admin_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    League Admins:{' '}
                    {audienceData.league_admin_users.toLocaleString()} User
                    {audienceData.league_admin_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.official_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Officials: {audienceData.official_users.toLocaleString()}{' '}
                    User
                    {audienceData.official_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.videographer_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Videographers:{' '}
                    {audienceData.videographer_users.toLocaleString()} User
                    {audienceData.videographer_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.following_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Followers: {audienceData.following_users.toLocaleString()}{' '}
                    User
                    {audienceData.following_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.player_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Players: {audienceData.player_users.toLocaleString()} User
                    {audienceData.player_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.team_official_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Team Officials:{' '}
                    {audienceData.team_official_users.toLocaleString()} User
                    {audienceData.team_official_users > 1 ? 's' : ''}
                  </Text>
                )}
                <div
                  css={css`
                    margin-top: 8px;
                    padding-top: 8px;
                    border-top: 1px solid ${colors.SECONDARY_100};
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='semiBold'
                    color={colors.WHITE}
                  >
                    Total: {audienceData.total_users.toLocaleString()} Unique
                    Users
                  </Text>
                </div>
              </div>
            ) : (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                Unable to load audience data
              </Text>
            )}
          </div>

          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Are you sure you want to continue?
            </Text>
          </div>
        </div>

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: center;
            gap: 12px;
            display: flex;
            flex-direction: column;
          `}
        >
          <Button
            text={isLoading ? 'POSTING...' : 'SEND NOTIFICATIONS'}
            onClick={onConfirm}
            disabled={isLoading}
            isLoading={isLoading}
            variant='primary'
            style={css`
              width: 100%;
            `}
          />
          <Button
            text='CANCEL'
            onClick={onClose}
            disabled={isLoading}
            variant='secondary'
            style={css`
              width: 100%;
            `}
          />
        </div>
      </div>
    </Modal>
  )
}
